import { Suspense, useMemo, useRef } from 'react';
import { Environment, Lightformer } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import type { AmbientLight, DirectionalLight } from 'three';
import { FLOOR_RADIUS } from '../model/stageGround';
import { DrivingGround } from './DrivingGround';
import { WeatherFog, WeatherLights } from './WeatherAppliers';
import type { TimeOfDayOption } from '../data/timeOfDayOptions';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { StudioOption } from '../types';
import type { RuntimeWeather } from '../model/weatherTransition';

const FLOOR_Y = -0.002;

interface StudioProps {
  studio: StudioOption;
  /** 식별자가 고정된 가변 객체입니다. 값은 프레임 루프에서 읽습니다. */
  weather: RuntimeWeather;
}

interface EnvironmentProps extends StudioProps {
  timeOfDay: TimeOfDayOption;
  /** 그래픽 품질이 정하는 환경맵 해상도입니다. */
  environmentResolution?: number;
}

/**
 * 환경맵 노출을 프레임마다 씬에 직접 씁니다.
 *
 * drei Environment에 intensity를 props로 주면 날씨 전환 샘플마다 트리가 재조정되고,
 * 절차적 배경에서는 children 정체성이 바뀌어 큐브맵을 다시 굽습니다(6면 렌더).
 * scene의 노출 값만 갱신하면 같은 결과를 훨씬 싸게 얻습니다.
 */
function EnvironmentExposure({
  weather,
  timeOfDay,
  baseBackground,
  baseEnvironment,
  suppressBackground,
}: {
  weather: RuntimeWeather;
  timeOfDay: TimeOfDayOption;
  baseBackground: number;
  baseEnvironment: number;
  suppressBackground: boolean;
}) {
  const scene = useThree((state) => state.scene);

  useFrame(() => {
    scene.environmentIntensity =
      baseEnvironment *
      weather.environmentIntensityMultiplier *
      timeOfDay.environmentMultiplier;
    scene.backgroundIntensity = suppressBackground
      ? 0
      : baseBackground *
        weather.backgroundIntensityMultiplier *
        timeOfDay.backgroundMultiplier;
  });

  return null;
}

/** 사진 환경 로딩 중에도 보이는 단색 배경과 Lightformer 환경맵입니다. */
function ProceduralEnvironment({
  studio,
  weather,
  timeOfDay,
  environmentResolution = 256,
}: EnvironmentProps) {
  const background = timeOfDay.isDark ? timeOfDay.skyColor : studio.backgroundColor;
  /*
   * Lightformer 배열을 memo로 고정합니다. drei Environment는 children 정체성이 바뀌면
   * 굽기 effect를 다시 실행하므로, 매 렌더 새 배열을 만들면 큐브맵이 계속 다시 구워집니다.
   * 날씨·시간대 노출은 EnvironmentExposure가 씬에 직접 씁니다.
   */
  const lightformers = useMemo(
    () =>
      studio.lightformers.map((lightformer, index) => (
        // 프리셋 안 순서가 고정된 정적 배열입니다.
        // eslint-disable-next-line react/no-array-index-key
        <Lightformer key={index} {...lightformer} />
      )),
    [studio.lightformers]
  );

  return (
    <>
      <color attach="background" args={[background]} />
      <Environment key={`${studio.id}:procedural`} resolution={environmentResolution} frames={1}>
        {lightformers}
      </Environment>
      <EnvironmentExposure
        weather={weather}
        timeOfDay={timeOfDay}
        baseBackground={1}
        baseEnvironment={1}
        suppressBackground={false}
      />
    </>
  );
}

interface PhotographicEnvironmentProps extends EnvironmentProps {
  studio: StudioOption & { environment: NonNullable<StudioOption['environment']> };
  flatGround: boolean;
}

/** 밤에는 주간 HDR 배경을 감추고, 약한 반사 광원으로만 유지합니다. */
function PhotographicEnvironment({
  studio,
  weather,
  timeOfDay,
  flatGround,
}: PhotographicEnvironmentProps) {
  const {
    files,
    backgroundBlurriness = 0,
    backgroundIntensity = 1,
    environmentIntensity = 1,
    rotation = [0, 0, 0],
    ground,
  } = studio.environment;

  return (
    <>
      {timeOfDay.isDark ? <color attach="background" args={[timeOfDay.skyColor]} /> : null}
      <Environment
        key={ground ? `${studio.id}:${files}:${flatGround ? 'flat' : 'projected'}` : `${studio.id}:${files}`}
        files={files}
        background={!timeOfDay.isDark}
        backgroundBlurriness={backgroundBlurriness}
        backgroundRotation={rotation}
        environmentRotation={rotation}
        ground={flatGround ? undefined : ground}
      />
      <EnvironmentExposure
        weather={weather}
        timeOfDay={timeOfDay}
        baseBackground={backgroundIntensity}
        baseEnvironment={environmentIntensity}
        suppressBackground={timeOfDay.isDark}
      />
    </>
  );
}

function StudioFloor({ studio }: Pick<StudioProps, 'studio'>) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, FLOOR_Y, 0]}>
      <circleGeometry args={[FLOOR_RADIUS, 64]} />
      <meshStandardMaterial
        color={studio.floorColor}
        roughness={studio.floorRoughness}
        metalness={studio.floorMetalness}
      />
    </mesh>
  );
}

interface StageProps extends StudioProps {
  driving: boolean;
  drivingCourse?: DrivingCourseOption;
  anisotropy?: number;
}

function StageGround({ studio, weather, driving, drivingCourse, anisotropy }: StageProps) {
  return driving ? (
    <DrivingGround weather={weather} course={drivingCourse} anisotropy={anisotropy} />
  ) : (
    <StudioFloor studio={studio} />
  );
}

interface ProceduralStageProps extends StageProps {
  timeOfDay: TimeOfDayOption;
  environmentResolution?: number;
}

function ProceduralStage({
  studio,
  weather,
  timeOfDay,
  driving,
  drivingCourse,
  anisotropy,
  environmentResolution,
}: ProceduralStageProps) {
  return (
    <>
      <ProceduralEnvironment
        studio={studio}
        weather={weather}
        timeOfDay={timeOfDay}
        environmentResolution={environmentResolution}
      />
      <StageGround
        studio={studio}
        weather={weather}
        driving={driving}
        drivingCourse={drivingCourse}
        anisotropy={anisotropy}
      />
    </>
  );
}

export interface StudioEnvironmentProps extends EnvironmentProps {
  /** 주행 모드에서는 HDR 지면 투영 대신 실제 주행장을 사용합니다. */
  flatGround?: boolean;
  /** 그래픽 품질이 정하는 노면 텍스처 비등방 필터링 상한입니다. */
  anisotropy?: number;
  /** 주행 중 렌더할 선택 코스입니다. */
  drivingCourse?: DrivingCourseOption;
}

/** 배경, 날씨, 시간대, 조명, 환경맵과 바닥을 한 장면으로 구성합니다. */
export function StudioEnvironment({
  studio,
  weather,
  timeOfDay,
  flatGround = false,
  anisotropy,
  drivingCourse,
  environmentResolution,
}: StudioEnvironmentProps) {
  const environment = studio.environment;
  const driving = flatGround;
  const ambientRef = useRef<AmbientLight | null>(null);
  const keyLightRef = useRef<DirectionalLight | null>(null);
  const fillLightRef = useRef<DirectionalLight | null>(null);

  return (
    <>
      <WeatherFog weather={weather} timeOfDay={timeOfDay} />
      <WeatherLights
        weather={weather}
        timeOfDay={timeOfDay}
        ambientRef={ambientRef}
        keyLightRef={keyLightRef}
        fillLightRef={fillLightRef}
        baseAmbient={studio.ambientIntensity}
        baseKey={studio.keyLight.intensity}
        baseFill={studio.fillLight.intensity}
      />
      {/* 세기는 WeatherLights가 매 프레임 씁니다. 여기서는 위치·색만 정합니다. */}
      <ambientLight ref={ambientRef} intensity={studio.ambientIntensity} />
      <directionalLight
        ref={keyLightRef}
        position={timeOfDay.sunPosition}
        color={timeOfDay.sunColor}
        intensity={studio.keyLight.intensity}
      />
      <directionalLight
        ref={fillLightRef}
        position={studio.fillLight.position}
        color={timeOfDay.isDark ? '#6f86bd' : '#ffffff'}
        intensity={studio.fillLight.intensity}
      />

      {environment ? (
        <Suspense
          fallback={
            <ProceduralStage
              studio={studio}
              weather={weather}
              timeOfDay={timeOfDay}
              driving={driving}
              drivingCourse={drivingCourse}
              anisotropy={anisotropy}
              environmentResolution={environmentResolution}
            />
          }
        >
          <PhotographicEnvironment
            studio={{ ...studio, environment }}
            weather={weather}
            timeOfDay={timeOfDay}
            flatGround={flatGround}
          />
          {environment.ground && !driving ? null : (
            <StageGround
              studio={studio}
              weather={weather}
              driving={driving}
              drivingCourse={drivingCourse}
              anisotropy={anisotropy}
            />
          )}
        </Suspense>
      ) : (
        <ProceduralStage
          studio={studio}
          weather={weather}
          timeOfDay={timeOfDay}
          driving={driving}
          drivingCourse={drivingCourse}
          anisotropy={anisotropy}
          environmentResolution={environmentResolution}
        />
      )}
    </>
  );
}
